import { useEffect, useState } from "react";

const PRIVACY_POLICY = {
  badge: "LEGAL",
  title: "Privacy Policy",
  updated: "April 10, 2026",
  intro:
    "This Privacy Policy is mock content for layout and development purposes. It explains how Paladin Professional Insurance Solutions may collect, use, and safeguard information when you interact with our website and services.",
  sections: [
    {
      heading: "Information We Collect",
      paragraphs: [
        "We may collect personal details you provide directly, including your name, email address, phone number, mailing address, and insurance-related inquiry details.",
        "We may also collect basic technical data such as browser type, device information, pages visited, and referral source to improve website performance and user experience.",
      ],
    },
    {
      heading: "How We Use Information",
      paragraphs: [
        "Information is used to respond to requests, provide quotes, support policy servicing, and communicate updates relevant to your account or inquiry.",
        "We may use aggregated usage data for analytics, internal reporting, and service improvements.",
      ],
    },
    {
      heading: "How We Share Information",
      paragraphs: [
        "We do not sell personal information. We may share data with insurance carriers, service providers, and partners only as needed to fulfill your request or support operations.",
        "We may disclose information when required by law, legal process, or to protect rights, safety, and security.",
      ],
    },
    {
      heading: "Data Security and Retention",
      paragraphs: [
        "Reasonable administrative, technical, and physical safeguards are used to protect personal data from unauthorized access, use, or disclosure.",
        "Data is retained only as long as necessary for business, legal, and regulatory purposes.",
      ],
    },
    {
      heading: "Your Choices",
      paragraphs: [
        "You may contact us to request access, correction, or deletion of certain personal information, subject to applicable legal and regulatory requirements.",
        "You may also opt out of non-essential marketing communications at any time.",
      ],
    },
  ],
};

const TERMS_OF_SERVICE = {
  badge: "LEGAL",
  title: "Terms of Service",
  updated: "April 10, 2026",
  intro:
    "These Terms of Service are mock content for layout and development purposes. By using this website, you agree to these general terms and conditions.",
  sections: [
    {
      heading: "Use of This Website",
      paragraphs: [
        "You agree to use this website for lawful purposes only and in a manner that does not infringe the rights of others or restrict their use of the site.",
        "You must not attempt to disrupt site functionality, bypass security controls, or misuse forms and contact channels.",
      ],
    },
    {
      heading: "No Professional Advice",
      paragraphs: [
        "Website content is provided for general informational purposes and does not constitute legal, financial, or insurance advice.",
        "Coverage terms, pricing, and eligibility are determined by the issuing carrier and applicable policy documents.",
      ],
    },
    {
      heading: "Quotes and Coverage",
      paragraphs: [
        "Any quote request submitted through this website is non-binding and subject to underwriting review and carrier approval.",
        "Coverage is not in effect until all required documentation is completed and the carrier confirms policy issuance.",
      ],
    },
    {
      heading: "Intellectual Property",
      paragraphs: [
        "All website text, branding, logos, and design elements are owned by or licensed to Paladin Professional Insurance Solutions unless otherwise stated.",
        "You may not copy, reproduce, or distribute website content for commercial use without prior written permission.",
      ],
    },
    {
      heading: "Limitation of Liability",
      paragraphs: [
        "To the maximum extent permitted by law, Paladin Professional Insurance Solutions is not liable for indirect, incidental, or consequential damages arising from use of this website.",
        "Your sole remedy for dissatisfaction with the website is to discontinue use.",
      ],
    },
  ],
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS = new Set(["Mon", "Tue", "Wed", "Thu", "Fri"]);
const DAY_HOURS = (day) => WEEKDAYS.has(day) ? "9:00 AM – 5:00 PM" : "Closed";

function LegalModal({ data, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black-deep/75 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full sm:max-w-2xl max-h-[92dvh] sm:max-h-[85vh] flex flex-col rounded-t-3xl sm:rounded-2xl overflow-hidden bg-white-warm">

        {/* Modal Header */}
        <div className="flex-shrink-0 px-8 pt-8 pb-5 border-b border-blue-dark/10">
          <span className="inline-block text-xs tracking-[0.2em] font-cinzel font-bold px-3 py-1 rounded-full mb-3 text-blue-primary border-[1.5px] border-blue-primary">
            {data.badge}
          </span>
          <h2 className="font-cinzel font-bold text-3xl sm:text-4xl text-blue-dark leading-tight">
            {data.title}
          </h2>
          <p className="font-constantia text-xs tracking-widest uppercase text-blue-dark/50 mt-2">
            Last Updated: {data.updated}
          </p>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          <p className="font-constantia text-base text-blue-dark/80 leading-relaxed">
            {data.intro}
          </p>
          <div className="h-px bg-blue-dark/10" />
          {data.sections.map((sec, i) => (
            <div key={i} className="space-y-3">
              <h3 className="font-cinzel font-bold text-base text-blue-dark">{sec.heading}</h3>
              {sec.paragraphs.map((p, j) => (
                <p key={j} className="font-constantia text-sm text-blue-dark/75 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          ))}
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-blue-dark text-white-warm transition-all duration-200 hover:scale-110"
          aria-label="Close modal"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// SVG Icons
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 5.65 5.65l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z" />
  </svg>
);
const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const SOCIAL_LINKS = [
  {
    Icon: FacebookIcon,
    href: "https://www.facebook.com/people/Paladin-Professional-Insurance-Solutions/61573336433957/",
    label: "Facebook",
  },
  {
    Icon: InstagramIcon,
    href: "https://www.instagram.com/accounts/login/?next=%2Fpaladin_professional_insurance%2F&source=omni_redirect",
    label: "Instagram",
  },
  {
    Icon: LinkedInIcon,
    href: "https://www.linkedin.com/authwall?trk=bf&trkInfo=AQFliXQhh-Im_AAAAZ4kgCNwYzoeP4HdsmENJVV1e07gNeoORQBfmPwOftACj2KK9wjtjpTbSj5jDTMMYt4EyqC65KTNLQl-B5C6PJX1qBZjroWNK3Sr6XgTXpUqqVArkWWkbQY=&original_referer=&sessionRedirect=https%3A%2F%2Fwww.linkedin.com%2Fcompany%2Fpaladin-professional-insurance-solutions%2Fabout%2F",
    label: "LinkedIn",
  },
];

export default function Footer() {
  const [modal, setModal] = useState(null);
  const today = DAYS[new Date().getDay()];

  return (
    <>
      {/* ── MODAL ── */}
      {modal === "privacy" && (
        <LegalModal data={PRIVACY_POLICY} onClose={() => setModal(null)} />
      )}
      {modal === "terms" && (
        <LegalModal data={TERMS_OF_SERVICE} onClose={() => setModal(null)} />
      )}

      <footer className="bg-white-warm">

        {/* ══ CTA BANNER ══ */}
        <div className="relative overflow-hidden bg-blue-dark">
          {/* Decorative diagonal stripe */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(120deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 40px)",
            }}
          />
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="space-y-1">
              <h2 className="font-cinzel font-bold text-2xl sm:text-3xl text-white-pure leading-snug">
                Need Support?
              </h2>
              <p className="font-constantia text-white-pure/70 text-sm sm:text-base max-w-md leading-relaxed">
                Have a question or need assistance? Our team is here to help you find the right insurance solution.
              </p>
            </div>
            <a
              href="mailto:support@PaladinBusinessServices.net"
              className="flex-shrink-0 group inline-flex items-center gap-2 px-7 py-3 rounded-full font-cinzel font-bold text-sm tracking-wide bg-white-warm text-blue-dark shadow-[0_4px_24px_rgba(0,0,0,0.18)] transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              Contact Support
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>

        {/* ══ MAIN FOOTER BODY ══ */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 grid grid-cols-1 lg:grid-cols-2 gap-14">

          {/* ── LEFT: Contact Details ── */}
          <div className="space-y-8">
            <div>
              <span className="inline-block w-8 h-0.5 mb-3 bg-blue-primary" />
              <h3 className="font-cinzel font-bold text-lg text-blue-dark tracking-wide">
                Contact Details
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Address */}
              <div className="space-y-1">
                <p className="font-cinzel text-xs font-bold tracking-[0.15em] uppercase text-blue-primary">
                  Address
                </p>
                <p className="font-constantia text-blue-dark/80 text-sm leading-relaxed">
                  3787 Transport ST Suite A7 Box #5,<br />Ventura, CA 93003
                </p>
              </div>

              {/* Phone */}
              <div className="space-y-1">
                <p className="font-cinzel text-xs font-bold tracking-[0.15em] uppercase text-blue-primary">
                  Phone
                </p>
                <a
                  href="tel:8056926900"
                  className="inline-flex items-center gap-2 font-constantia text-sm text-blue-dark/80 hover:text-blue-primary transition-colors duration-200 group"
                >
                  <span className="opacity-60 group-hover:opacity-100 transition-opacity"><PhoneIcon /></span>
                  805-692-6900
                </a>
              </div>

              {/* Fax */}
              <div className="space-y-1">
                <p className="font-cinzel text-xs font-bold tracking-[0.15em] uppercase text-blue-primary">
                  Fax
                </p>
                <p className="font-constantia text-blue-dark/80 text-sm">805-830-1680</p>
              </div>

              {/* Email */}
              <div className="space-y-1">
                <p className="font-cinzel text-xs font-bold tracking-[0.15em] uppercase text-blue-primary">
                  Email
                </p>
                <a
                  href="mailto:support@PaladinBusinessServices.net"
                  className="inline-flex items-center gap-2 font-constantia text-sm text-blue-dark/80 hover:text-blue-primary transition-colors duration-200 group break-all"
                >
                  <span className="opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0"><MailIcon /></span>
                  support@PaladinBusinessServices.net
                </a>
              </div>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3 pt-2">
              {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full flex items-center justify-center border-[1.5px] border-blue-dark text-blue-dark bg-transparent hover:bg-blue-dark hover:text-white-warm transition-all duration-300 hover:scale-110 hover:shadow-lg"
                >
                  <Icon />
                </a>
              ))}
            </div>

            {/* License and Agency Info Block */}
            <div className="pt-4 mt-2 border-t border-blue-dark/12 space-y-1">
              <p className="font-times text-xs tracking-widest uppercase text-blue-primary">
                Agency License No.: <span className="font-bold">6010043</span>
              </p>
              <p className="font-constantia text-xs text-blue-dark/50 tracking-wide">
                Licensed in CA, AZ, ID, IL, IN, NV, NC, OH and TX
              </p>
              <p className="font-constantia text-xs text-blue-dark/50 italic">
                Paladin Professional Insurance Solutions &nbsp;|&nbsp; Protecting What Matters, With Care That Shows
              </p>
            </div>
          </div>

          {/* ── RIGHT: Office Hours ── */}
          <div className="space-y-8">
            <div>
              <span className="inline-block w-8 h-0.5 mb-3 bg-blue-primary" />
              <h3 className="font-cinzel font-bold text-lg text-blue-dark tracking-wide">
                Office Hours
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DAYS.map((day) => {
                const isToday = day === today;
                return (
                  <div
                    key={day}
                    className={`
                      flex items-center justify-between px-5 py-3 rounded-xl transition-all duration-200
                      ${isToday ? "bg-blue-dark text-white-warm" : "bg-blue-dark/[0.06] text-blue-dark"}
                    `}
                  >
                    <span className={`font-cinzel text-xs font-bold tracking-widest uppercase ${isToday ? "opacity-100" : "opacity-70"}`}>
                      {day}
                    </span>
                    <span className={`font-times text-xs ${isToday ? "opacity-90" : "opacity-65"}`}>
                      {DAY_HOURS(day)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ══ BOTTOM BAR ══ */}
        <div className="border-t border-blue-dark/12">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="font-constantia text-xs text-blue-dark/45 text-center sm:text-left">
              © 2026 Paladin Professional Insurance Solutions. All Rights Reserved.
            </p>
            <div className="flex items-center gap-1">
              {[
                { label: "Privacy Policy", action: () => setModal("privacy") },
                { label: "Terms of Service", action: () => setModal("terms") },
                { label: "Disclaimer", action: () => setModal("disclaimer") },
              ].map(({ label, action }, i, arr) => (
                <span key={label} className="flex items-center gap-1">
                  <button
                    onClick={action}
                    className="font-constantia text-xs text-blue-dark/50 hover:text-blue-primary transition-colors duration-200 underline-offset-2 hover:underline"
                  >
                    {label}
                  </button>
                  {i < arr.length - 1 && (
                    <span className="text-blue-dark/20 text-xs select-none">|</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>

      </footer>
    </>
  );
}